import React, { useState, useEffect } from 'react';
import { Grid, Header, Button, Dropdown, Loader } from 'semantic-ui-react';
import { CreateSessionAction } from '../controller/actions';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Decimal from 'decimal.js';

export default function CreateSessionPage(){
    const dispatch = useDispatch();
    const [disableNextBtn, setDisableNextBtn] = useState(true);
    const [surchargeAmount, setSurchargeAmount] = useState(0.0);
    const [total, setTotal] = useState(0.0);
    const [isPageError, setIsPageError] = useState(false);
    const [loader, setLoader] = useState(false);
    const [cardType, setCardType] = useState(null);

    const CARD_OPTIONS = [
        {
            key: 'amex',
            value: 'TA - Amex',
            surcharge: 2.15,
            text: 'Amex (2.15%)',
        },
        {
            key: 'visaDebit',
            value: 'T2 - Visa Debit',
            surcharge: 0.9,
            text: 'Visa Debit (0.9%)',
        },
        {
            key: 'visaCredit',
            value: 'TV - Visa Credit',
            surcharge: 1.5,
            text: 'Visa Credit (1.5%)',
        },
        {
            key: 'mastercardDebit',
            value: 'T1 - Mastercard Debit',
            surcharge: 0.9,
            text: 'Mastercard Debit (0.9%)',
        },
        {
            key: 'mastercardCredit',
            value: 'TM - Mastercard Credit',
            surcharge: 1.5,
            text: 'Mastercard Credit (1.5%)',
        },
    ];

    function useQuery() {
        return new URLSearchParams(useLocation().search);
    }
    
    const query = useQuery();

    const [transactionId, amount, region, txType, defaultPaymentMethod] = ['transactionId', 'amount', 'region', 'txType', 'defaultPaymentMethod'].map((param) => query.get(param) ?? null);
    const isNewZealandPage = region === 'NZ';
    const takePaymentOnAuth = txType === 'purchase'; // true for all purchase, false if 'validation'
    const isDefaultPaymentMethod = defaultPaymentMethod === 'true';
    
    function onCardChange(event, data) {
        setCardType(data.value);
    
        if (data.value) {
            setDisableNextBtn(false);
        }
    }

    function onSubmit(){
        if (transactionId) {
            setLoader(true);
            dispatch(new CreateSessionAction().requestHPPLink(transactionId, surchargeAmount, cardType))
                .then(() => {
                    console.log('TODO: redirect to received HPP link');
                })
                .catch((error) => {
                    console.error('Failed to request HPP link:', error);
                    setLoader(false);
                    setIsPageError(true);
                });
        } else {
            setIsPageError(true);
        }
    }

    function roundToTwoDecimals(number) {
        return Math.round((number + Number.EPSILON) * 100) / 100;
    }
    
    useEffect(() => {
        if (!cardType) {
            return;
        }
        const selectedCardType = CARD_OPTIONS.find((option) => option.value === cardType);
        const preciseAmount = new Decimal(amount || 0.0);
        const surchargeMultiplier = new Decimal(selectedCardType.surcharge).dividedBy(100);
        const calculatedSurcharge = preciseAmount.times(surchargeMultiplier).toDecimalPlaces(2, Decimal.ROUND_HALF_DOWN);
        const calculatedAmount = preciseAmount.plus(calculatedSurcharge).toDecimalPlaces(2, Decimal.ROUND_HALF_DOWN);
    
        setSurchargeAmount(calculatedSurcharge.toNumber());
        setTotal(calculatedAmount.toNumber());
    }, [cardType]);


    return (
        <Grid verticalAlign="middle" centered className="content">
            <Grid.Row centered>
                {
                    isPageError &&
                    <Grid.Column>
                        <Header size="large">Something went wrong! Please contact help desk.</Header>
                    </Grid.Column>
                }
                {
                    !isPageError &&
                    <Grid.Column>
                        <Header size="large">CoreLogic Secure Payment</Header>
                        {(amount && !isNewZealandPage) ?
                            <>
                                <br/>
                                <p className="payment-info"><b>Payment</b> - ${roundToTwoDecimals(parseFloat(amount))}</p>
                                <p className="payment-info"><b>Credit Card Surcharge</b> - ${roundToTwoDecimals(surchargeAmount)}</p>
                                <p className="payment-info"><b>Total Payment</b> - ${roundToTwoDecimals(total)}</p>
                                <br/>
                            </>
                            :
                            <>
                                {
                                    amount > 0 &&
                                    <>
                                        <br/>
                                        <p><b>Payment</b> - ${parseFloat(amount).toFixed(2)}</p>
                                        <br/>
                                    </>
                                }
                            </>
                        }
                        <p>
                            Select your card type and then proceed to the next page to securely
                            provide your card details and validate payment.
                        </p>
                        <Header size="medium">Card Type {!isNewZealandPage && '(Fees Apply)'}</Header>
                        <Dropdown
                            fluid
                            selection
                            placeholder="-- Select Card Type --"
                            options={isNewZealandPage 
                                ? CARD_OPTIONS.filter((option) => option.key !== 'amex') 
                                : CARD_OPTIONS
                            }
                            onChange={onCardChange}
                            value={cardType}
                        />
                        {
                            (isDefaultPaymentMethod || !takePaymentOnAuth) &&
                            <p className="note">
                                { (isDefaultPaymentMethod || !takePaymentOnAuth) && 'Please note:' }
                                <ul>
                                    { (!takePaymentOnAuth) && 
                                        <li>If you proceed, a temporary pre-authorisation amount, as disclosed on the next payment page, will be added to your card for verification purposes. This amount will be released within 7 days.</li> 
                                    }
                                    { (isDefaultPaymentMethod) && 
                                        <li>These updated payment details apply to all CoreLogic products associated with your account.</li> 
                                    }
                                </ul>
                            </p>
                        }
                        <div style={{ 'marginTop': '10px' }}>
                            <Button className={disableNextBtn || (loader) ? 'disabled-button' : 'primary'}onClick={onSubmit}>
                                Next {(loader) && <Loader active inline size="tiny" />}
                            </Button>
                        </div>
                    </Grid.Column>
                }
            </Grid.Row>
        </Grid>
    );
}